import { useEffect, useRef } from 'react';
import { forEach, isNil, mapValues } from 'lodash';

interface RequestObject {
    cancel: (reason?: string) => void;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    request: (...args: any[]) => Promise<unknown>;
}

type RequestFactories = Record<string, () => RequestObject>;

type RequestObjects<F extends RequestFactories> = { [K in keyof F]: ReturnType<F[K]> };

interface Options<F extends RequestFactories> {
    /**
     * Keys whose previous in-flight call is aborted when a new call starts, so only the latest
     * response reaches the callbacks. Use it for list, filter, paging and search requests.
     * Leave out requests that are fired in parallel (for example inside `Promise.all`).
     */
    takeLatest?: Extract<keyof F, string>[];
}

/**
 * Creates request objects once per component and aborts all their in-flight calls on unmount,
 * replacing the `useMemo(() => ({ ... }), [])` + `useEffect(cleanUp, [])` pattern.
 *
 * @example
 * const { getListRequest, deleteRequest } = useCancelableRequests(
 *     { getListRequest: ClientTagHttpService.getClientTagsList, deleteRequest: ClientTagHttpService.deleteClientTag },
 *     { takeLatest: ['getListRequest'] },
 * );
 */
const useCancelableRequests = <F extends RequestFactories>(
    factories: F,
    options: Options<F> = {},
): RequestObjects<F> => {
    const requestsRef = useRef<RequestObjects<F>>();

    if (isNil(requestsRef.current)) {
        const takeLatestKeys = new Set<string>(options.takeLatest ?? []);

        requestsRef.current = mapValues(factories, (factory, key) => {
            const requestObject = factory();

            if (!takeLatestKeys.has(key)) {
                return requestObject;
            }

            return {
                cancel: requestObject.cancel,
                request: (...args) => {
                    requestObject.cancel(`${key}: superseded by a newer call`);
                    return requestObject.request(...args);
                },
            };
        }) as RequestObjects<F>;
    }

    useEffect(() => {
        const requests = requestsRef.current;

        return () => {
            forEach(requests, (requestObject: RequestObject, key) => requestObject.cancel(`${key}: unmount`));
        };
    }, []);

    return requestsRef.current;
};

export default useCancelableRequests;
